import React, { useState, useEffect } from 'react';
import { Shield, Users, ToggleLeft, Calendar, Send, Car, CircleDot, RefreshCw, Plus, Trash2, Sparkles, MapPin, X } from 'lucide-react';
import { zoneSyncError, zoneSyncMessage } from '../lib/zone-sync';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  { val: 'auto', label: 'Auto',  hint: 'Show only if data exists', cls: 'bg-gray-100 text-gray-700' },
  { val: 'on',   label: 'On',    hint: 'Always show this card',    cls: 'bg-green-100 text-green-700' },
  { val: 'off',  label: 'Off',   hint: 'Always hide this card',    cls: 'bg-red-100 text-red-700' },
];

const CAMPAIGN_STATES = [
  { val: 'under_review', label: 'Under review', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  { val: 'in_progress',  label: 'In progress',  cls: 'bg-green-100 text-green-700 border-green-200' },
  { val: 'completed',    label: 'Completed',    cls: 'bg-navy/10 text-navy border-navy/20' },
];

const Segmented = ({ value, onChange, testid }) => (
  <div className="inline-flex rounded-lg border border-navy/15 overflow-hidden" data-testid={testid}>
    {STATES.map(s => (
      <button
        key={s.val}
        onClick={() => onChange(s.val)}
        title={s.hint}
        className={`px-3 py-1 text-xs font-semibold transition ${
          value === s.val ? s.cls : 'bg-white text-navy/50 hover:bg-cream'
        }`}
        data-testid={`${testid}-${s.val}`}
      >
        {s.label}
      </button>
    ))}
  </div>
);

// ── Zones editor: adjust targeted premium zones after campaign creation ──
const ZonesEditor = ({ advertiser, onSave }) => {
  const [zones, setZones] = useState(
    (advertiser.mini_zones || []).map((z) => ({ ...z }))
  );
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (i, field, value) => {
    const next = [...zones];
    next[i] = { ...next[i], [field]: value };
    setZones(next);
    setDirty(true);
  };
  const addZone = () => {
    setZones([...zones, { name: `Zone ${zones.length + 1}`, lat: '', lng: '', radius: 0.5 }]);
    setDirty(true);
  };
  const removeZone = (i) => {
    setZones(zones.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    const ok = await onSave(zones.map((z) => ({
      name: z.name,
      lat: parseFloat(z.lat),
      lng: parseFloat(z.lng),
      radius: parseFloat(z.radius),
    })));
    setSaving(false);
    if (ok) setDirty(false);
  };

  return (
    <div className="min-w-0 md:col-span-3" data-testid={`zones-editor-${advertiser.id}`}>
      <label className="text-xs text-navy/50 font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
        <MapPin size={12} /> Targeted Zones ({zones.length})
      </label>
      {zones.length === 0 && (
        <p className="text-xs text-navy/40 italic mb-1.5">No premium zones — drivers earn standard-zone impressions only.</p>
      )}
      <div className="space-y-1.5">
        {zones.map((z, i) => (
          <div key={i} className="flex flex-wrap items-center gap-1.5">
            <input type="text" value={z.name || ''} placeholder="Name"
                   onChange={(e) => update(i, 'name', e.target.value)}
                   className="px-2 py-1.5 border border-navy/20 rounded-md text-xs w-36"
                   data-testid={`zone-name-${advertiser.id}-${i}`} />
            <input type="number" step="any" value={z.lat} placeholder="Latitude"
                   onChange={(e) => update(i, 'lat', e.target.value)}
                   className="px-2 py-1.5 border border-navy/20 rounded-md text-xs w-28"
                   data-testid={`zone-lat-${advertiser.id}-${i}`} />
            <input type="number" step="any" value={z.lng} placeholder="Longitude"
                   onChange={(e) => update(i, 'lng', e.target.value)}
                   className="px-2 py-1.5 border border-navy/20 rounded-md text-xs w-28"
                   data-testid={`zone-lng-${advertiser.id}-${i}`} />
            <div className="flex items-center gap-1">
              <input type="number" step="0.05" min="0.05" max="10" value={z.radius ?? z.radius_miles ?? 0.5}
                     onChange={(e) => update(i, 'radius', e.target.value)}
                     className="px-2 py-1.5 border border-navy/20 rounded-md text-xs w-20"
                     data-testid={`zone-radius-${advertiser.id}-${i}`} />
              <span className="text-[10px] text-navy/40">mi</span>
            </div>
            <a href={`https://www.google.com/maps?q=${z.lat},${z.lng}`} target="_blank" rel="noreferrer"
               className="text-[10px] text-navy/50 underline" title="Preview center on Google Maps" data-testid={`zone-map-${advertiser.id}-${i}`}>map</a>
            <button onClick={() => removeZone(i)} className="text-red-500 hover:bg-red-50 p-1 rounded"
                    data-testid={`zone-remove-${advertiser.id}-${i}`}>
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <button onClick={addZone}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-navy border border-navy/20 rounded-md hover:bg-cream"
                data-testid={`zone-add-${advertiser.id}`}>
          <Plus size={12} /> Add zone
        </button>
        {dirty && (
          <button onClick={save} disabled={saving}
                  className="px-3 py-1.5 text-xs font-semibold bg-navy text-cream rounded-md disabled:opacity-50"
                  data-testid={`zones-save-${advertiser.id}`}>
            {saving ? 'Saving…' : 'Save zones'}
          </button>
        )}
      </div>
      <p className="text-[10px] text-navy/40 italic mt-1">
        Tip: right-click a spot in Google Maps to copy its lat, lng. After saving, click
        <strong> “Resync to driver app”</strong> above to push the updated zones to driver campaigns.
      </p>
    </div>
  );
};

// ── Milestones editor: add/edit/delete attribution milestones ──────────
// Each milestone anchors a phase on the advertiser's Conversions chart.
// Server auto-sorts by date and assigns colors when saved.
const genLocalId = () => (window.crypto?.randomUUID?.() || `local-${Date.now()}-${Math.random()}`);

const MilestonesEditor = ({ advertiser, drivers, onSave }) => {
  const [rows, setRows] = useState(() => advertiser.milestones || []);
  const [dirty, setDirty] = useState(false);

  // Refresh local state when the parent-passed advertiser record changes
  // (e.g. after another admin action reloads the list).
  useEffect(() => {
    setRows(advertiser.milestones || []);
    setDirty(false);
  }, [advertiser.milestones]);

  const updateRow = (id, patch) => {
    setRows(rs => rs.map(r => r.id === id ? { ...r, ...patch } : r));
    setDirty(true);
  };
  const removeRow = (id) => {
    setRows(rs => rs.filter(r => r.id !== id));
    setDirty(true);
  };
  const addRow = (row = {}) => {
    setRows(rs => [...rs, {
      id: genLocalId(),
      label: row.label || '',
      kind: row.kind || 'channel',
      started_at: row.started_at || new Date().toISOString().slice(0, 10),
      ended_at: row.ended_at || null,
    }]);
    setDirty(true);
  };
  const seedFromDrivers = () => {
    if (!drivers || drivers.length === 0) return;
    const existingLabels = new Set(rows.map(r => r.label));
    const additions = drivers
      .filter(d => d.joined_at)
      .map((d, i) => ({
        id: genLocalId(),
        label: `Driver ${i + 1} · ${d.name || d.email || 'Driver'}`,
        kind: 'driver',
        started_at: (d.joined_at || '').slice(0, 10),
      }))
      .filter(m => m.started_at && !existingLabels.has(m.label));
    if (additions.length === 0) return;
    setRows(rs => [...rs, ...additions]);
    setDirty(true);
  };
  const save = () => {
    // Strip local ids; backend regenerates real uuids for new rows on save.
    const payload = rows.map(r => ({
      id: r.id && !String(r.id).startsWith('local-') ? r.id : undefined,
      label: r.label,
      kind: r.kind,
      started_at: r.started_at,
      ended_at: r.ended_at || null,
      color: r.color,
    }));
    onSave(payload);
    setDirty(false);
  };

  const anyInvalid = rows.some(r => !r.label || !r.started_at);
  const hasDrivers = drivers && drivers.length > 0;

  return (
    <div className="min-w-0 md:col-span-3 border border-navy/15 rounded-lg bg-cream-light/40 p-3" data-testid={`milestones-editor-${advertiser.id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <label className="text-xs text-navy/70 font-semibold uppercase tracking-wide flex items-center gap-1">
          <Calendar size={12} /> Attribution Milestones
          <span className="ml-1 text-navy/40 font-normal normal-case tracking-normal">
            (Baseline = 90 days before earliest milestone)
          </span>
        </label>
        <div className="flex items-center gap-2">
          {hasDrivers && (
            <button
              type="button"
              onClick={seedFromDrivers}
              className="inline-flex items-center gap-1 text-xs bg-white border border-navy/20 hover:bg-cream text-navy px-2 py-1 rounded"
              data-testid={`milestones-seed-drivers-${advertiser.id}`}
              title="Add one row per driver, dated when they joined"
            >
              <Sparkles size={12} /> Add from drivers
            </button>
          )}
          <button
            type="button"
            onClick={() => addRow()}
            className="inline-flex items-center gap-1 text-xs bg-navy text-cream hover:bg-navy-dark px-2 py-1 rounded"
            data-testid={`milestones-add-${advertiser.id}`}
          >
            <Plus size={12} /> Add milestone
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-amber-700 italic">
          ⚠ No milestones set — lift % is hidden. Add at least one (a driver launch date works great) to unlock the Conversions chart.
        </p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={r.id} className="flex items-center gap-2 text-xs flex-wrap" data-testid={`milestone-row-${i}`}>
              <span
                className="w-3 h-3 rounded-full border border-white shadow-sm shrink-0"
                style={{ backgroundColor: r.color || '#94a3b8' }}
                title={r.color || 'color assigned on save'}
              />
              <select
                value={r.kind}
                onChange={(e) => updateRow(r.id, { kind: e.target.value })}
                className="border border-navy/20 rounded px-1 py-1 bg-white"
                data-testid={`milestone-kind-${i}`}
              >
                <option value="driver">Driver</option>
                <option value="channel">Channel</option>
              </select>
              <input
                type="text"
                value={r.label}
                onChange={(e) => updateRow(r.id, { label: e.target.value })}
                placeholder={r.kind === 'driver' ? 'Driver 1 · Name' : 'Facebook Ads'}
                className="flex-1 border border-navy/20 rounded px-2 py-1 min-w-[140px]"
                data-testid={`milestone-label-${i}`}
              />
              <div className="flex items-center gap-1">
                <label className="text-navy/50 uppercase tracking-wide text-[10px] font-semibold">Start</label>
                <input
                  type="date"
                  value={r.started_at}
                  onChange={(e) => updateRow(r.id, { started_at: e.target.value })}
                  className="border border-navy/20 rounded px-1 py-1"
                  data-testid={`milestone-date-${i}`}
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-navy/50 uppercase tracking-wide text-[10px] font-semibold">End</label>
                <input
                  type="date"
                  value={r.ended_at || ''}
                  onChange={(e) => updateRow(r.id, { ended_at: e.target.value || null })}
                  min={r.started_at || undefined}
                  className="border border-navy/20 rounded px-1 py-1"
                  placeholder="(ongoing)"
                  title="Leave blank for milestones that are still active"
                  data-testid={`milestone-end-date-${i}`}
                />
                {r.ended_at && (
                  <button
                    type="button"
                    onClick={() => updateRow(r.id, { ended_at: null })}
                    className="text-navy/40 hover:text-navy/70 text-[10px] underline"
                    title="Clear end date (mark as ongoing)"
                    data-testid={`milestone-clear-end-${i}`}
                  >
                    clear
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeRow(r.id)}
                className="text-red-500 hover:bg-red-50 rounded p-1"
                aria-label="Remove milestone"
                data-testid={`milestone-remove-${i}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {dirty && (
        <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-navy/10">
          <span className="text-xs text-amber-700 italic">Unsaved changes</span>
          <button
            type="button"
            onClick={save}
            disabled={anyInvalid}
            className={`text-xs px-3 py-1 rounded font-semibold ${
              anyInvalid ? 'bg-navy/30 text-cream/70 cursor-not-allowed'
                         : 'bg-navy text-cream hover:bg-navy-dark'
            }`}
            data-testid={`milestones-save-${advertiser.id}`}
          >
            Save milestones
          </button>
        </div>
      )}
    </div>
  );
};

const AdminAdvertisers = () => {
  const [adminKey, setAdminKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [syncingAdvertiser, setSyncingAdvertiser] = useState('');
  const [driversByAdv, setDriversByAdv] = useState({}); // id → drivers[]
  const [openDrivers, setOpenDrivers] = useState({}); // id → bool

  const headers = () => ({ 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setAdvertisers(data.advertisers || []);
      }
    } catch (err) {
      console.error('Failed to load advertisers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        setAuthed(true);
        load();
      } else {
        setMessage('Incorrect admin key');
      }
    } catch (err) {
      console.error('Admin auth failed:', err);
      setMessage('Unable to reach server. Try again.');
    }
  };

  const setMetric = async (adv, metric, val) => {
    const prev = adv.metrics_visibility?.[metric] || 'auto';
    // Optimistic update
    setAdvertisers(advertisers.map(a =>
      a.id === adv.id
        ? { ...a, metrics_visibility: { ...(a.metrics_visibility || {}), [metric]: val } }
        : a
    ));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}/metrics-visibility`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ [metric]: val }),
      });
      if (!res.ok) {
        // Rollback
        setAdvertisers(advertisers.map(a =>
          a.id === adv.id
            ? { ...a, metrics_visibility: { ...(a.metrics_visibility || {}), [metric]: prev } }
            : a
        ));
        setMessage('Save failed');
      } else {
        setMessage(`Updated ${adv.company_name} → ${metric === 'revenue_lift' ? 'Revenue Lift' : 'Other Lift'}: ${val}`);
      }
    } catch {
      setMessage('Network error');
    }
  };

  const setCampaignStart = async (adv, dateStr) => {
    const prev = adv.campaign_start_date || '';
    setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, campaign_start_date: dateStr } : a));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}/campaign-start`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ campaign_start_date: dateStr || null }),
      });
      if (!res.ok) {
        setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, campaign_start_date: prev } : a));
        const err = await res.json().catch(() => ({}));
        setMessage(err.detail || 'Save failed');
      } else {
        setMessage(`Campaign start for ${adv.company_name} → ${dateStr || 'cleared'}`);
      }
    } catch {
      setMessage('Network error');
    }
  };

  // ── Milestones (attribution timeline) ────────────────────────────────
  // Save the full list at once (server sorts + assigns colors).
  const saveMilestones = async (adv, milestones) => {
    const prev = adv.milestones || [];
    setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, milestones } : a));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}/milestones`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ milestones }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, milestones: prev } : a));
        setMessage(data.detail || 'Milestone save failed');
      } else {
        setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, milestones: data.milestones || [] } : a));
        setMessage(`Milestones updated for ${adv.company_name} (${(data.milestones || []).length} total)`);
      }
    } catch {
      setMessage('Network error');
    }
  };

  const setCampaignFields = async (adv, patch) => {
    const prev = { wrap_type: adv.wrap_type, duration_months: adv.duration_months };
    setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, ...patch } : a));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}/campaign-fields`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify(patch),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage(`${adv.company_name}: rate now $${(data.rate_per_hour || 0).toFixed(3)}/hr`);
      } else {
        setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, ...prev } : a));
        setMessage(data.detail || 'Save failed');
      }
    } catch {
      setMessage('Network error');
    }
  };

  const setManualEntry = async (adv, enabled, fields = {}) => {
    const prev = {
      manual_entry_enabled: adv.manual_entry_enabled,
      manual_entry_label: adv.manual_entry_label,
      manual_entry_secondary_label: adv.manual_entry_secondary_label,
      manual_entry_revenue_label: adv.manual_entry_revenue_label,
      manual_entry_note_label: adv.manual_entry_note_label,
    };
    const payload = { enabled, ...fields };
    setAdvertisers(advertisers.map(a => a.id === adv.id ? {
      ...a,
      manual_entry_enabled: enabled,
      ...(fields.label !== undefined ? { manual_entry_label: fields.label } : {}),
      ...(fields.secondary_label !== undefined ? { manual_entry_secondary_label: fields.secondary_label } : {}),
      ...(fields.revenue_label !== undefined ? { manual_entry_revenue_label: fields.revenue_label } : {}),
      ...(fields.note_label !== undefined ? { manual_entry_note_label: fields.note_label } : {}),
    } : a));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}/manual-entry`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setMessage(`${adv.company_name}: manual entry ${enabled ? 'updated' : 'disabled'}`);
      } else {
        setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, ...prev } : a));
        setMessage('Save failed');
      }
    } catch {
      setMessage('Network error');
    }
  };

  const sendTestDigest = async (adv) => {
    if (!window.confirm(`Send weekly digest email to ${adv.email}?`)) return;
    setMessage(`Sending digest to ${adv.email}…`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/digests/send-test/${adv.id}`, {
        method: 'POST', headers: headers(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.status === 'sent') setMessage(`Digest sent to ${adv.email}`);
        else if (data.status === 'skipped') setMessage(`Skipped: ${data.reason}`);
        else setMessage(`SMTP failed for ${adv.email}`);
      } else {
        setMessage(data.detail || 'Send failed');
      }
    } catch {
      setMessage('Network error');
    }
  };

  const toggleDrivers = async (adv) => {
    const willOpen = !openDrivers[adv.id];
    setOpenDrivers({ ...openDrivers, [adv.id]: willOpen });
    if (willOpen && !driversByAdv[adv.id]) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}/drivers`, { headers: headers() });
        if (res.ok) {
          const d = await res.json();
          setDriversByAdv(prev => ({ ...prev, [adv.id]: d.drivers || [] }));
        }
      } catch { /* ignore */ }
    }
  };

  const saveZones = async (adv, zones) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}/mini-zones`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ mini_zones: zones }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setAdvertisers(advertisers.map(x => x.id === adv.id ? { ...x, mini_zones: data.mini_zones } : x));
        setMessage(`${adv.company_name}: ${(data.mini_zones || []).length} zone(s) saved — click "Resync to driver app" to push them.`);
        return true;
      }
      setMessage(data.detail || 'Zone save failed');
      return false;
    } catch {
      setMessage('Network error');
      return false;
    }
  };

  const deleteAdvertiser = async (adv) => {
    const answer = window.prompt(
      `⚠️ This permanently deletes "${adv.company_name}" (${adv.email}) and ALL its data — sessions, conversions, drivers, integrations, chat history. NO UNDO.\n\nType DELETE to confirm:`
    );
    if (answer !== 'DELETE') return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}`, {
        method: 'DELETE', headers: headers(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setAdvertisers(advertisers.filter(x => x.id !== adv.id));
        setMessage(`Deleted ${adv.company_name} and all related data.`);
      } else {
        setMessage(data.detail || 'Delete failed');
      }
    } catch {
      setMessage('Network error');
    }
  };

  const resyncAdTimeAdmin = async (adv) => {
    if (syncingAdvertiser) return;
    if (!window.confirm(`Sync saved zones for ${adv.company_name} (${adv.email})? This replaces premium zones on driver campaigns matching this advertiser email.`)) return;
    setSyncingAdvertiser(adv.id);
    setMessage('Syncing saved zones to the driver app…');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}/resync-ad-time-admin`, {
        method: 'POST', headers: headers(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage(zoneSyncMessage(data).text);
      } else {
        setMessage(zoneSyncError(data));
      }
    } catch {
      setMessage('Connection interrupted; the sync result is unknown. Check the driver campaign before retrying.');
    } finally {
      setSyncingAdvertiser('');
    }
  };

  const setCampaignStatus = async (adv, val) => {
    const prev = adv.campaign_status || 'under_review';
    setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, campaign_status: val } : a));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers/${adv.id}/campaign-status`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ campaign_status: val }),
      });
      if (!res.ok) {
        setAdvertisers(advertisers.map(a => a.id === adv.id ? { ...a, campaign_status: prev } : a));
        setMessage('Save failed');
      } else {
        setMessage(`${adv.company_name} → ${CAMPAIGN_STATES.find(s => s.val === val)?.label}`);
      }
    } catch {
      setMessage('Network error');
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <form onSubmit={handleLogin} className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-sm">
          <div className="flex flex-col items-center mb-4">
            <Shield className="text-navy mb-2" size={32} />
            <h1 className="text-xl font-bold text-navy">Admin Access</h1>
          </div>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key"
            autoFocus
            className="w-full px-3 py-2.5 border border-navy/20 rounded-lg mb-3 focus:border-navy focus:outline-none"
            data-testid="admin-key-input"
          />
          {message && <p className="text-red-600 text-sm mb-3" data-testid="login-error">{message}</p>}
          <button type="submit" className="w-full bg-navy text-cream py-2.5 rounded-lg font-semibold hover:bg-navy/90" data-testid="login-btn">
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <section className="bg-navy py-6">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row flex-wrap items-start sm:items-center justify-between gap-4" data-testid="admin-advertisers-heading">
          <h1 className="text-2xl font-bold text-cream flex items-start gap-2 min-w-0" data-testid="admin-advertisers-title">
            <Users size={22} className="shrink-0 mt-1" /> Advertiser Metrics Visibility
          </h1>
          <nav aria-label="Admin navigation" className="flex flex-wrap items-center gap-3 text-sm min-w-0" data-testid="admin-advertisers-navigation">
            <a href="/admin/geofences" style={{ color: 'var(--cream)' }} className="underline hover:opacity-80" data-testid="admin-advertisers-nav-geofences">Geofences</a>
            <a href="/admin/manual-data" style={{ color: 'var(--cream)' }} className="underline hover:opacity-80" data-testid="admin-advertisers-nav-manual-data">Manual data</a>
            <a href="/admin/leads" style={{ color: 'var(--cream)' }} className="underline hover:opacity-80" data-testid="admin-advertisers-nav-leads">Leads</a>
            <a href="/admin/idle-heatmap" style={{ color: 'var(--cream)' }} className="underline hover:opacity-80" data-testid="admin-advertisers-nav-idle-heatmap">Idle heatmap</a>
            <span className="text-cream" data-testid="admin-advertisers-account-count">{advertisers.length} account(s)</span>
          </nav>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl p-4 mb-4 border border-navy/10 text-sm text-navy/70">
          <p className="flex items-start gap-2">
            <ToggleLeft size={18} className="text-navy mt-0.5 flex-shrink-0" />
            <span>
              Control which lift cards each advertiser sees on their Conversions tab.
              <strong> Auto</strong> = show only if data exists (default).
              <strong> On</strong> = always show, even with zero data.
              <strong> Off</strong> = always hide.
            </span>
          </p>
        </div>

        {message && (
          <div role="status" aria-live="polite" className="bg-navy/5 text-navy text-sm px-4 py-2 rounded-lg mb-3 break-words" data-testid="status-message">{message}</div>
        )}

        <div className="space-y-4">
          {loading ? (
            <p className="p-8 text-center text-navy/50 bg-white rounded-2xl">Loading…</p>
          ) : advertisers.length === 0 ? (
            <p className="p-8 text-center text-navy/50 bg-white rounded-2xl">No advertisers yet.</p>
          ) : (
            advertisers.map((a) => {
              const v = a.metrics_visibility || { revenue_lift: 'auto', new_clients_lift: 'auto' };
              const status = a.campaign_status || 'under_review';
              const statusMeta = CAMPAIGN_STATES.find(s => s.val === status) || CAMPAIGN_STATES[0];
              const drivers = a.drivers || [];
              return (
                <div key={a.id} className="bg-white rounded-2xl shadow-sm overflow-hidden"
                     data-testid={`advertiser-row-${a.id}`}>
                  {/* Header row */}
                  <div className="px-5 py-4 border-b border-navy/10 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 max-w-full">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-bold text-navy text-base break-words min-w-0" data-testid={`advertiser-company-${a.id}`}>{a.company_name || '—'}</h3>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusMeta.cls}`}>
                          {statusMeta.label}
                        </span>
                        {a.terms_accepted_at ? (
                          <span
                            className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 border-emerald-300 text-emerald-700"
                            title={`Accepted ${new Date(a.terms_accepted_at).toLocaleString()}${a.terms_version ? ` (v${a.terms_version})` : ''}`}
                            data-testid={`terms-status-${a.id}`}
                          >
                            ✓ Terms · {new Date(a.terms_accepted_at).toLocaleDateString()}
                          </span>
                        ) : (
                          <span
                            className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-50 border-amber-300 text-amber-700"
                            title="Advertiser has not yet accepted Terms of Service & Privacy Policy"
                            data-testid={`terms-status-${a.id}`}
                          >
                            ⚠ Terms pending
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-navy/50 mt-0.5 break-all" data-testid={`advertiser-email-${a.id}`}>{a.email}</p>
                      <p className="text-xs text-navy/70 mt-1" data-testid={`advertiser-driver-count-${a.id}`}>
                        Campaign cars: {a.driver_count || 1}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 max-w-full min-w-0" data-testid={`advertiser-actions-${a.id}`}>
                      <div className="inline-flex rounded-lg border border-navy/15 overflow-hidden" data-testid={`campaign-status-${a.id}`}>
                        {CAMPAIGN_STATES.map(s => (
                          <button
                            key={s.val}
                            onClick={() => setCampaignStatus(a, s.val)}
                            className={`px-3 py-1.5 text-xs font-semibold transition ${
                              status === s.val ? s.cls : 'bg-white text-navy/50 hover:bg-cream'
                            }`}
                            data-testid={`campaign-status-${a.id}-${s.val}`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => sendTestDigest(a)}
                        title="Send weekly digest email now (test)"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-navy border border-navy/20 rounded-md hover:bg-cream"
                        data-testid={`send-digest-${a.id}`}
                      >
                        <Send size={12} /> Send digest
                      </button>
                      <button
                        onClick={() => resyncAdTimeAdmin(a)}
                        disabled={Boolean(syncingAdvertiser)}
                        aria-busy={syncingAdvertiser === a.id}
                        title="Push campaign + zones + driver count to ad-time-admin"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-navy border border-navy/20 rounded-md hover:bg-cream disabled:opacity-50"
                        data-testid={`resync-ad-time-${a.id}`}
                      >
                        <RefreshCw size={12} className={syncingAdvertiser === a.id ? 'animate-spin' : ''} /> {syncingAdvertiser === a.id ? 'Syncing…' : 'Resync to driver app'}
                      </button>
                      <button
                        onClick={() => deleteAdvertiser(a)}
                        title="Permanently delete this campaign and ALL its data"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                        data-testid={`delete-advertiser-${a.id}`}
                      >
                        <Trash2 size={12} /> Delete campaign
                      </button>
                      <button
                        onClick={() => toggleDrivers(a)}
                        title="View drivers who joined this campaign"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-navy border border-navy/20 rounded-md hover:bg-cream"
                        data-testid={`view-drivers-${a.id}`}
                      >
                        <Users size={12} /> {openDrivers[a.id] ? 'Hide drivers' : 'View drivers'}
                      </button>
                    </div>
                  </div>

                  {/* Drivers list (lazy-loaded, collapsible) */}
                  {openDrivers[a.id] && (
                    <div className="px-5 py-4 bg-cream/30 border-t border-navy/5" data-testid={`drivers-list-${a.id}`}>
                      <h4 className="text-xs uppercase tracking-wider font-semibold text-navy/60 mb-3">
                        Joined drivers ({(driversByAdv[a.id] || []).length})
                      </h4>
                      {(driversByAdv[a.id] || []).length === 0 ? (
                        <p className="text-sm text-navy/50 italic">No drivers have joined this campaign yet.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-navy/60 uppercase tracking-wider">
                                <th className="text-left py-2 pr-3">Name</th>
                                <th className="text-left py-2 pr-3">Email</th>
                                <th className="text-left py-2 pr-3">Vehicle</th>
                                <th className="text-left py-2 pr-3">Joined</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(driversByAdv[a.id] || []).map((dr) => {
                                const v = dr.vehicle || {};
                                const vehicleStr = [v.year, v.color, v.make, v.model].filter(Boolean).join(' ') || '—';
                                const joined = dr.joined_at ? new Date(dr.joined_at).toLocaleDateString() : '—';
                                return (
                                  <tr key={dr.driver_email} className="border-t border-navy/5">
                                    <td className="py-2 pr-3 font-semibold text-navy">{dr.driver_name || '—'}</td>
                                    <td className="py-2 pr-3 text-navy/70">{dr.driver_email}</td>
                                    <td className="py-2 pr-3 text-navy/70">{vehicleStr}</td>
                                    <td className="py-2 pr-3 text-navy/70">{joined}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fields grid */}
                  <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-5">
                    <MilestonesEditor
                      advertiser={a}
                      drivers={driversByAdv[a.id] || []}
                      onSave={(payload) => saveMilestones(a, payload)}
                    />
                    <ZonesEditor
                      key={`zones-${a.id}-${(a.mini_zones || []).length}`}
                      advertiser={a}
                      onSave={(zones) => saveZones(a, zones)}
                    />
                    <div>
                      <label className="text-xs text-navy/50 font-semibold uppercase tracking-wide mb-1.5 block">Wrap type</label>
                      <select
                        value={a.wrap_type || 'Full Wrap'}
                        onChange={(e) => setCampaignFields(a, { wrap_type: e.target.value })}
                        className="px-2 py-1.5 border border-navy/20 rounded-md text-xs bg-white"
                        data-testid={`wrap-type-${a.id}`}
                      >
                        <option value="Full Wrap">Full Wrap</option>
                        <option value="Partial Wrap">Partial Wrap</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-navy/50 font-semibold uppercase tracking-wide mb-1.5 block">Duration (months)</label>
                      <select
                        value={a.duration_months || 3}
                        onChange={(e) => setCampaignFields(a, { duration_months: parseInt(e.target.value) })}
                        className="px-2 py-1.5 border border-navy/20 rounded-md text-xs bg-white"
                        data-testid={`duration-months-${a.id}`}
                      >
                        {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} months</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-navy/50 font-semibold uppercase tracking-wide mb-1.5 block">Revenue Lift card</label>
                      <Segmented
                        value={v.revenue_lift || 'auto'}
                        onChange={(val) => setMetric(a, 'revenue_lift', val)}
                        testid={`revenue-lift-${a.id}`}
                      />
                      <p className="text-xs text-navy/40 mt-1">
                        Controls <strong>Revenue Lift</strong> and <strong>Website Visits Lift</strong> together.
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-navy/50 font-semibold uppercase tracking-wide mb-1.5 block">Other Lift card</label>
                      <Segmented
                        value={v.new_clients_lift || 'auto'}
                        onChange={(val) => setMetric(a, 'new_clients_lift', val)}
                        testid={`new-clients-lift-${a.id}`}
                      />
                      <p className="text-xs text-navy/40 mt-1">
                        Controls the <strong>{a.manual_entry_label || 'Other'} Lift</strong> card (the optional manual-entry metric).
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-navy/50 font-semibold uppercase tracking-wide mb-1.5 block">Manual Entry</label>
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!a.manual_entry_enabled}
                            onChange={(e) => setManualEntry(a, e.target.checked)}
                            className="w-4 h-4 accent-navy"
                            data-testid={`manual-entry-toggle-${a.id}`}
                          />
                          <span className="text-xs font-semibold text-navy">
                            {a.manual_entry_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </label>
                      </div>
                      {a.manual_entry_enabled && (
                        <div className="mt-2 space-y-1.5">
                          <input
                            type="text"
                            placeholder="Other parameter label (e.g. New memberships)"
                            defaultValue={a.manual_entry_label || ''}
                            onBlur={(e) => {
                              const next = e.target.value.trim();
                              if (next !== (a.manual_entry_label || '')) {
                                setManualEntry(a, true, { label: next });
                              }
                            }}
                            className="w-full px-2 py-1.5 border border-navy/20 rounded-md text-xs"
                            data-testid={`manual-entry-label-${a.id}`}
                          />
                          <input
                            type="text"
                            placeholder="Revenue label (default: Revenue ($))"
                            defaultValue={a.manual_entry_revenue_label || ''}
                            onBlur={(e) => {
                              const next = e.target.value.trim();
                              if (next !== (a.manual_entry_revenue_label || '')) {
                                setManualEntry(a, true, { revenue_label: next });
                              }
                            }}
                            className="w-full px-2 py-1.5 border border-navy/20 rounded-md text-xs"
                            data-testid={`manual-entry-revenue-${a.id}`}
                          />
                          <p className="text-[10px] text-navy/40 italic">
                            Leave blank for default. Each label changes the column header on the advertiser&apos;s form.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Driver roster strip */}
                  <div className="px-5 py-3 border-t border-navy/5 bg-cream/40">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="text-xs text-navy/50 font-semibold uppercase tracking-wide flex items-center gap-1 mr-1">
                        <Car size={12} /> Drivers ({drivers.length})
                      </span>
                      {drivers.length === 0 ? (
                        <span className="text-xs text-navy/40 italic">None assigned yet</span>
                      ) : (
                        drivers.map((d, idx) => (
                          <span
                            key={d.id}
                            title={d.started ? 'Has completed at least one session' : 'Not started yet'}
                            className={`text-xs font-semibold px-2.5 py-1 rounded-full border flex items-center gap-1 ${
                              d.started
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                            }`}
                            data-testid={`driver-pill-${a.id}-${idx}`}
                          >
                            <CircleDot size={10} />
                            Driver {idx + 1}: {d.name}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAdvertisers;
